import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { certificateService } from '@/services/certificateService';
import type { PublicCertificateVerification } from '@/types/certificate';

type VerificationState = 'loading' | 'success' | 'error' | 'not_found' | 'no_token';

export const VerifyCertificatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState<VerificationState>('loading');
  const [verification, setVerification] = useState<PublicCertificateVerification | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setState('no_token');
      return;
    }

    const verifyToken = async () => {
      try {
        setState('loading');
        const result = await certificateService.publicVerify(token);
        setVerification(result);
        setState('success');
      } catch (error) {
        if (error instanceof Error && error.message.includes('not found')) {
          setState('not_found');
        } else {
          setState('error');
          setErrorMessage(error instanceof Error ? error.message : 'Verification failed');
        }
      }
    };

    verifyToken();
  }, [token]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderNoToken = () => (
    <div className="text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">Token Tidak Ditemukan</h2>
      <p className="text-gray-600 mb-6">
        Silakan masukkan token verifikasi sertifikat atau pindai QR code yang terdapat pada sertifikat.
      </p>
      <Link
        to="/"
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Kembali ke Beranda
      </Link>
    </div>
  );

  const renderLoading = () => (
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Memverifikasi sertifikat...</p>
    </div>
  );

  const renderNotFound = () => (
    <div className="text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">Sertifikat Tidak Ditemukan</h2>
      <p className="text-gray-600 mb-6">
        Token verifikasi tidak valid atau sertifikat tidak ada dalam sistem kami.
      </p>
      <Link
        to="/"
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Kembali ke Beranda
      </Link>
    </div>
  );

  const renderError = () => (
    <div className="text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">Terjadi Kesalahan</h2>
      <p className="text-gray-600 mb-6">{errorMessage || 'Gagal memverifikasi sertifikat. Silakan coba lagi.'}</p>
      <Link
        to="/"
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Kembali ke Beranda
      </Link>
    </div>
  );

  const renderSuccess = () => {
    if (!verification) return null;

    const isValid = verification.valid;
    const statusInfo = {
      issued: { label: 'Valid', bgColor: 'bg-green-100', textColor: 'text-green-800', icon: 'check' },
      revoked: { label: 'Dicabut', bgColor: 'bg-red-100', textColor: 'text-red-800', icon: 'x' },
      expired: { label: 'Kedaluwarsa', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', icon: 'clock' },
    };

    const status = statusInfo[verification.status] || statusInfo.issued;

    return (
      <div>
        {/* Status Banner */}
        <div className={`rounded-lg p-4 mb-6 ${isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            {isValid ? (
              <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <div>
              <h3 className={`font-semibold ${isValid ? 'text-green-800' : 'text-red-800'}`}>
                {isValid ? 'Sertifikat Terverifikasi' : 'Sertifikat Tidak Valid'}
              </h3>
              <p className={`text-sm ${isValid ? 'text-green-700' : 'text-red-700'}`}>
                {isValid
                  ? 'Sertifikat ini terdaftar dan valid dalam sistem kami.'
                  : verification.status === 'revoked'
                  ? 'Sertifikat ini telah dicabut.'
                  : 'Sertifikat ini telah kedaluwarsa.'}
              </p>
            </div>
          </div>
        </div>

        {/* Certificate Details */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-800">Detail Sertifikat</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Nama Pemegang</label>
                <p className="text-gray-900 font-medium">{verification.holder_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.textColor}`}>
                  {status.label}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Nama Kursus</label>
                <p className="text-gray-900">{verification.course_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Tanggal Terbit</label>
                <p className="text-gray-900">{formatDate(verification.issue_date)}</p>
              </div>
              {verification.expiry_date && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Tanggal Kedaluwarsa</label>
                  <p className="text-gray-900">{formatDate(verification.expiry_date)}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">ID Sertifikat</label>
                <p className="text-gray-900 font-mono text-sm">{verification.certificate_id}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Kembali ke Beranda
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Verifikasi Sertifikat</h1>
          <p className="mt-2 text-gray-600">
            Halaman ini digunakan untuk memverifikasi keaslian sertifikat.
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
          {state === 'loading' && renderLoading()}
          {state === 'no_token' && renderNoToken()}
          {state === 'not_found' && renderNotFound()}
          {state === 'error' && renderError()}
          {state === 'success' && renderSuccess()}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Butuh bantuan? Hubungi tim dukungan kami.</p>
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificatePage;
